import React, { useEffect, useState } from 'react';
import { BookOpen, Link2, RefreshCw } from 'lucide-react';
import apiService from '../apiService';

/**
 * Associer un menu (modèle de boutique : rayons, produits, prix, options) à un fournisseur.
 * Réutilise l'import en deux phases de l'app Business (POST /store-templates/:id/import) :
 *  1. rayons + groupes d'options (et vidage du catalogue si « Remplacer »),
 *  2. produits par lots de 25 (barre de progression).
 * Le commerce est lié au modèle (template_sync) → bannière « Mettre à jour le menu » côté Business.
 */
const LIMIT = 25;

export default function AssignMenuPanel({ supplier, productCount, onDone, canEdit }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('merge'); // 'merge' (ajouter) | 'replace' (remplacer tout)
  const [progress, setProgress] = useState(null); // { label, done, total }
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const linkedId = supplier?.template_sync?.template_id || null;

  useEffect(() => {
    if (!supplier?.business_type) return;
    let alive = true;
    // Paramètres dans l'URL : apiService.get de l'admin ignore l'objet de paramètres.
    apiService.get(`/admin/delivery/store-templates?business_type=${encodeURIComponent(supplier.business_type)}`, { userType: 'admin' })
      .then(r => { if (alive) setTemplates(r?.templates || []); })
      .catch(() => { if (alive) setTemplates([]); });
    return () => { alive = false; };
  }, [supplier?.business_type]);

  const linked = (templates || []).find(t => t.template_id === linkedId);

  const run = async () => {
    if (!selected) return;
    if (mode === 'replace' && !window.confirm(`Remplacer TOUT le catalogue de « ${supplier.business_name} » (${productCount} produit(s) actuels) par le menu « ${selected.name} » ?\n\nLes commandes passées gardent leurs copies. Cette action est irréversible.`)) return;
    setError(''); setResult(null);
    const base = { supplier_id: supplier.supplier_id, mode };
    try {
      setProgress({ label: mode === 'replace' ? 'Remplacement des rayons…' : 'Création des rayons…', done: 0, total: 0 });
      const c = await apiService.post(`/store-templates/${selected.template_id}/import`, { ...base, phase: 'categories' }, { userType: 'admin' });
      if (!c?.success) throw new Error(c?.error || 'Import des rayons impossible');
      const total = c.product_total || 0;
      let done = 0;
      setProgress({ label: 'Ajout des produits…', done, total });
      while (done < total) {
        const p = await apiService.post(`/store-templates/${selected.template_id}/import`, { ...base, phase: 'products', offset: done, limit: LIMIT }, { userType: 'admin' });
        if (!p?.success) throw new Error(p?.error || 'Import des produits interrompu');
        done = p.done ?? (done + LIMIT);
        setProgress({ label: 'Ajout des produits…', done: Math.min(done, total), total });
      }
      setProgress(null);
      setResult({ categories: c.created_categories ?? selected.category_count, options: c.created_option_groups || 0, products: total, name: selected.name, mode });
      setSelected(null);
      onDone && onDone();
    } catch (e) {
      setProgress(null);
      setError(e?.error || e?.message || 'Erreur pendant l\'import');
    }
  };

  const box = { background: 'white', borderRadius: 14, padding: '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
  const btn = (bg, color = 'white') => ({ padding: '0.55rem 1rem', borderRadius: 10, border: 'none', background: bg, color, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 });

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '1rem', fontWeight: 800, color: '#1E2229', display: 'flex', alignItems: 'center', gap: 8 }}><BookOpen size={18} /> Menu de la boutique</div>
          <div style={{ fontSize: '0.82rem', color: '#5C636E', marginTop: 4 }}>
            {productCount} produit(s) au catalogue
            {linkedId && <> · <Link2 size={12} style={{ verticalAlign: 'middle' }} /> lié au modèle <strong>{linked?.name || linkedId}</strong>{supplier.template_sync?.imported_at ? ` (importé le ${new Date(supplier.template_sync.imported_at).toLocaleDateString('fr-FR')})` : ''}</>}
          </div>
        </div>
        {canEdit && !open && <button style={btn('#F54119')} onClick={() => { setOpen(true); setResult(null); setError(''); }}><BookOpen size={15} /> Associer un menu</button>}
      </div>

      {result && (
        <div style={{ marginTop: 12, padding: '0.75rem 1rem', borderRadius: 10, background: '#ECFDF5', color: '#166534', fontSize: '0.88rem' }}>
          ✅ Menu « {result.name} » {result.mode === 'replace' ? 'installé' : 'ajouté'} : {result.categories} rayon(s), {result.products} produit(s){result.options ? `, ${result.options} groupe(s) d'options` : ''}. Le commerce verra « Mettre à jour le menu » si le modèle change.
        </div>
      )}
      {error && <div style={{ marginTop: 12, padding: '0.75rem 1rem', borderRadius: 10, background: '#FEF2F2', color: '#B91C1C', fontSize: '0.88rem' }}>{error}</div>}

      {open && (
        <div style={{ marginTop: 14 }}>
          {templates === null ? (
            <div style={{ color: '#9AA0A8', fontSize: '0.88rem' }}>Chargement des modèles…</div>
          ) : templates.length === 0 ? (
            <div style={{ color: '#9AA0A8', fontSize: '0.88rem' }}>
              Aucun modèle de boutique pour le type « {supplier.business_type} ». Créez-en un dans <strong>Modèles de boutique</strong> (à la main, ou « 🪄 Depuis un menu PDF/photo »).
            </div>
          ) : (
            <>
              <div style={{ fontSize: '0.8rem', color: '#5C636E', marginBottom: 8 }}>Modèles pour le type « {supplier.business_type} » :</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10, marginBottom: 12 }}>
                {templates.map(t => {
                  const on = selected?.template_id === t.template_id;
                  return (
                    <button key={t.template_id} onClick={() => setSelected(t)} disabled={!!progress}
                      style={{ textAlign: 'left', padding: '0.75rem 0.9rem', borderRadius: 12, cursor: 'pointer', background: on ? '#FEE9E2' : '#FFFFFF', border: `2px solid ${on ? '#F54119' : '#E9EBEF'}` }}>
                      <div style={{ fontWeight: 700, color: '#1E2229', fontSize: '0.9rem' }}>{t.name}{t.template_id === linkedId ? ' · lié' : ''}</div>
                      <div style={{ fontSize: '0.78rem', color: '#5C636E', marginTop: 3 }}>{t.category_count || 0} rayon(s) · {t.product_count || 0} produit(s)</div>
                      <div style={{ fontSize: '0.72rem', color: '#9AA0A8', marginTop: 2 }}>{t.visibility === 'public' ? 'Public' : 'Privé'}{t.source_business_name ? ` · source : ${t.source_business_name}` : ''}</div>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: '0.82rem', color: '#414852', fontWeight: 600 }}>Mode :</span>
                <button onClick={() => setMode('merge')} disabled={!!progress} style={btn(mode === 'merge' ? '#1E2229' : '#F4F5F7', mode === 'merge' ? 'white' : '#5C636E')}>Ajouter à l'existant</button>
                <button onClick={() => setMode('replace')} disabled={!!progress} style={btn(mode === 'replace' ? '#B91C1C' : '#F4F5F7', mode === 'replace' ? 'white' : '#5C636E')}>Remplacer tout le catalogue</button>
                <span style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>{mode === 'merge' ? 'Rayons fusionnés par nom, produits ajoutés.' : 'Le catalogue actuel est vidé puis remplacé.'}</span>
              </div>

              {progress ? (
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#414852', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><RefreshCw size={14} /> {progress.label}{progress.total ? ` ${progress.done}/${progress.total}` : ''}</div>
                  <div style={{ height: 8, background: '#F4F5F7', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: 8, width: progress.total ? `${(progress.done / progress.total) * 100}%` : '8%', background: '#F54119', transition: 'width .3s' }} />
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btn('#F4F5F7', '#414852')} onClick={() => { setOpen(false); setSelected(null); }}>Fermer</button>
                  <button style={{ ...btn(mode === 'replace' ? '#B91C1C' : '#F54119'), opacity: selected ? 1 : 0.5 }} disabled={!selected} onClick={run}>
                    {mode === 'replace' ? 'Remplacer par ce menu' : 'Ajouter ce menu'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
